import React, { useEffect, useMemo, useState } from "react";
import { Spin, Tabs, Radio } from "antd";
import { Chart, registerables } from "chart.js";
import { Line } from "react-chartjs-2";
import Bridge from "../../../utils/Bridge";
import { logEvent } from "../../../utils/analytics";
import helperFunctions from "../../../utils/functions";
import {
  buildPgiChartAxis,
  buildPgiChartOptions,
  buildPgiLineDataset,
  PGI_CHART_COLORS,
  PGI_CHART_SCALES,
} from "../../../utils/pgi";
import "./SpeechRating.css";

Chart.register(...registerables);

const { TabPane } = Tabs;

const PGI_GRAPH_TYPES = {
  SEVERITY: "pgi-s",
  CHANGE: "pgi-c",
};

const chartBackgroundPlugin = {
  id: "pgiChartBackground",
  beforeDraw: (chart) => {
    const ctx = chart.canvas.getContext("2d");
    const chartArea = chart.chartArea;
    if (!chartArea) return;
    ctx.save();
    ctx.fillStyle = "#FAFCFC";
    ctx.fillRect(
      chartArea.left,
      chartArea.top,
      chartArea.right - chartArea.left,
      chartArea.bottom - chartArea.top
    );
    ctx.restore();
  },
};

const SpeechRating = (props) => {
  const [list, setList] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("table");
  const [graphType, setGraphType] = useState(PGI_GRAPH_TYPES.SEVERITY);

  const formData = props.language;
  const copy = formData?.page?.personalDataViewer?.speechRating || {};

  useEffect(() => {
    const eals_id = localStorage.getItem("ap_eals_id");
    if (!eals_id) {
      return;
    }

    setLoading(true);
    const params = `?eals_id=${eals_id}`;

    Promise.all([
      Bridge.personal_data_viewer.le_study.get_pgi_list(params),
      Bridge.personal_data_viewer.le_study.get_pgi_graph(params),
    ])
      .then(([listRes, graphRes]) => {
        setList(listRes.status == "1" ? listRes.data || [] : []);
        setGraphData(graphRes.status == "1" ? graphRes.data || [] : []);
        setLoading(false);
      })
      .catch(() => {
        setList([]);
        setGraphData([]);
        setLoading(false);
      });
  }, []);

  const renderTable = () => {
    if (list.length === 0) {
      return (
        <div
          style={{
            fontSize: 20,
            textAlign: "center",
            padding: 20,
          }}
        >
          {formData.page.notAvailable}
        </div>
      );
    }

    return (
      <div style={{ overflowX: "auto", width: "100%" }}>
        <table style={{ minWidth: "600px", width: "100%" }}>
          <tr>
            <th style={{ minWidth: "120px" }}>{copy.date}</th>
            <th style={{ minWidth: "200px" }}>{copy.pgiS}</th>
            <th style={{ minWidth: "200px" }}>{copy.pgiC}</th>
          </tr>
          {list.map((row, index) => (
            <tr key={index}>
              <td style={{ minWidth: "120px" }}>{row.date || "---"}</td>
              <td
                className={helperFunctions.getPgiColor(row.pgi_s)}
                style={{ minWidth: "200px" }}
              >
                {row.pgi_s_display || "---"}
              </td>
              <td
                className={helperFunctions.getPgiColor(row.pgi_c)}
                style={{ minWidth: "200px" }}
              >
                {row.pgi_c_display || "---"}
              </td>
            </tr>
          ))}
        </table>
      </div>
    );
  };

  const sortedGraphData = useMemo(
    () =>
      [...graphData].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      ),
    [graphData]
  );

  const chartAxis = useMemo(
    () => buildPgiChartAxis(sortedGraphData),
    [sortedGraphData]
  );

  const isSeverityGraph = graphType === PGI_GRAPH_TYPES.SEVERITY;

  const activeSeriesMeta = useMemo(
    () =>
      isSeverityGraph ? chartAxis.getPgiSMeta() : chartAxis.getPgiCMeta(),
    [chartAxis, isSeverityGraph]
  );

  const lineChartData = useMemo(() => {
    const isSeverity = graphType === PGI_GRAPH_TYPES.SEVERITY;
    const seriesMeta = isSeverity
      ? chartAxis.getPgiSMeta()
      : chartAxis.getPgiCMeta();

    const color = isSeverity
      ? PGI_CHART_COLORS.severity
      : PGI_CHART_COLORS.change;

    return {
      datasets: [
        buildPgiLineDataset(seriesMeta, {
          label: isSeverity
            ? copy.speechSeverity || "Speech Severity"
            : copy.speechChange || "Speech Change",
          borderColor: color,
          backgroundColor: color,
          pointStyle: isSeverity ? "star" : "rect",
          pointRadius: isSeverity ? 8 : 7,
          pointBackgroundColor: color,
          pointBorderColor: color,
          pointBorderWidth: 1,
          pointHoverRadius: isSeverity ? 9 : 8,
        }),
      ],
    };
  }, [chartAxis, graphType, copy.speechSeverity, copy.speechChange]);

  const chartOptions = useMemo(
    () =>
      buildPgiChartOptions({
        seriesMeta: activeSeriesMeta,
        xAxisTitle: copy.dateAxis || "Date",
        yAxisTitle: isSeverityGraph
          ? copy.severityRatingAxis || "Speech Severity"
          : copy.changeRatingAxis || "Speech Change",
      }),
    [
      isSeverityGraph,
      activeSeriesMeta,
      copy.dateAxis,
      copy.severityRatingAxis,
      copy.changeRatingAxis,
    ]
  );

  const hasGraphDataForType = useMemo(() => {
    if (sortedGraphData.length === 0) {
      return false;
    }

    return sortedGraphData.some((row) => {
      const value = isSeverityGraph ? row.pgi_s : row.pgi_c;
      return value !== null && value !== undefined;
    });
  }, [sortedGraphData, isSeverityGraph]);

  const renderGraph = () => {
    return (
      <div className="speech-rating-graph-panel">
        <div className="speech-rating-radio-wrap">
          <Radio.Group
            value={graphType}
            onChange={(e) => setGraphType(e.target.value)}
            className="speech-rating-radio-group"
          >
            <Radio value={PGI_GRAPH_TYPES.SEVERITY}>
              {copy.speechSeverity || "Speech Severity"}
            </Radio>
            <Radio value={PGI_GRAPH_TYPES.CHANGE}>
              {copy.speechChange || "Speech Change"}
            </Radio>
          </Radio.Group>
        </div>

        {graphData.length === 0 || !hasGraphDataForType ? (
          <p className="text-center my-4">
            {copy.noGraphDataForType ||
              `${copy.noGraphData || "No PGI graph data available."} ${
                isSeverityGraph
                  ? copy.speechSeverity || "Speech Severity"
                  : copy.speechChange || "Speech Change"
              }.`}
          </p>
        ) : (
          <>
            <div className="speech-rating-chart-wrap">
              <Line
                data={lineChartData}
                options={chartOptions}
                plugins={[chartBackgroundPlugin]}
              />
            </div>

            <div className="speech-rating-scale-desc">
              <p className="speech-rating-scale-desc-title">
                {isSeverityGraph
                  ? copy.scaleDescTitle || PGI_CHART_SCALES.severity.title
                  : copy.scaleChangeDescTitle || PGI_CHART_SCALES.change.title}
              </p>
              <p className="speech-rating-scale-desc-text">
                {isSeverityGraph
                  ? copy.scaleDescText || PGI_CHART_SCALES.severity.text
                  : copy.scaleChangeDescText || PGI_CHART_SCALES.change.text}
              </p>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="als_frs_sessions">
      <div className="container">
        <div className="row">
          <div className="col-lg-12 px-0">
            <Spin spinning={loading}>
              <Tabs
                activeKey={activeTab}
                onChange={(key) => { const tabLabels = { table: "Table", graph: "Graph View" }; logEvent("tab_selected", { tab: tabLabels[key] || key, section: "speech_rating" }); setActiveTab(key); }}
                type="card"
              >
                <TabPane tab={copy.tableTab || "Table"} key="table">
                  {renderTable()}
                </TabPane>
                <TabPane tab={copy.graphTab || "Graph view"} key="graph">
                  {renderGraph()}
                </TabPane>
              </Tabs>
            </Spin>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpeechRating;
